export interface EsimOrderView {
  saleId: string;
  status: string;
  name?: string;
  dataGb?: number;
  dataMb?: number | null;
  validityDays?: number;
  priceInr?: number;
  orderRef?: string;
  emailMasked?: string;
  activationCode?: string;
  failureCode?: string;
  network?: string;
  supportUrl?: string;
}

export type EsimPhase =
  | { kind: 'loading' }
  | { kind: 'waiting'; order: EsimOrderView }
  | { kind: 'preparing'; order: EsimOrderView }
  | { kind: 'delayed'; order: EsimOrderView }
  | { kind: 'ready'; order: EsimOrderView }
  | { kind: 'sorting'; order: EsimOrderView; message: string }
  | { kind: 'not-found' }
  | { kind: 'network' };

const PREPARING = new Set(['paid', 'provisioning']);
const ISSUING_FAILURE = new Set(['insufficient_balance', 'rate_limited', 'timeout']);

export function isIssuingOrder(order: { status: string; failureCode?: string }): boolean {
  if (PREPARING.has(order.status)) return true;
  return order.status === 'paid_unfulfilled' && ISSUING_FAILURE.has(order.failureCode || '');
}

export function phaseFromOrder(order: EsimOrderView, elapsedMs: number): Exclude<EsimPhase, 'loading' | 'not-found' | 'network'> {
  if (order.status === 'pending_payment') return { kind: 'waiting', order };
  if (isIssuingOrder(order)) {
    return elapsedMs >= 60_000 ? { kind: 'delayed', order } : { kind: 'preparing', order };
  }
  if (order.status === 'ready' && order.activationCode) return { kind: 'ready', order };
  if (order.status === 'payment_failed') {
    return { kind: 'sorting', order, message: 'The payment did not go through. You have not been charged.' };
  }
  if (order.status === 'abandoned') return { kind: 'sorting', order, message: 'You have not been charged.' };
  if (order.status === 'expired') return { kind: 'sorting', order, message: 'This order expired.' };
  if (order.status === 'refunded') return { kind: 'sorting', order, message: 'This order was refunded.' };
  return { kind: 'sorting', order, message: "We're sorting this out" };
}

export function pollDelayMs(attempt: number): number {
  return attempt < 2 ? 2000 : 5000;
}

export function shouldPoll(kind: EsimPhase['kind']): boolean {
  return kind === 'waiting' || kind === 'preparing' || kind === 'delayed';
}

export const ESIM_SALE_KEY = 'cwy-esim-sale';
export const celebratedKey = (saleId: string) => `cwy-esim-celebrated:${saleId}`;
